
import { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { addYears } from "date-fns";
import { ContractFormValues } from "../../form/ContractFormSchema";
import { useCurrencies } from "@/hooks/useCurrencies";
import { ContractType } from "@/lib/types";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

interface RequiredFinancialDetailsSectionProps {
  form: UseFormReturn<ContractFormValues>;
  setCurrencyModalOpen: (open: boolean) => void;
  servicesTotal?: number;
  selectedContractType: ContractType | null;
}

export function RequiredFinancialDetailsSection({ 
  form, 
  setCurrencyModalOpen,
  servicesTotal,
  selectedContractType
}: RequiredFinancialDetailsSectionProps) {
  const { t } = useEnhancedTranslation();
  const { useCurrenciesQuery } = useCurrencies();
  const { data: currencies = [] } = useCurrenciesQuery();
  
  const [costLabel, setCostLabel] = useState<string>('Amount');
  const isIncomingContract = selectedContractType?.direction === 'incoming';
  
  // Update cost label based on contract type
  useEffect(() => {
    if (selectedContractType?.direction === 'incoming') {
      setCostLabel(t('income', 'Income'));
    } else {
      setCostLabel(t('cost', 'Cost'));
    }
  }, [selectedContractType, t]);
  
  // Set default dates
  useEffect(() => {
    if (!form.getValues('signedDate')) {
      form.setValue('signedDate', new Date());
    }
    
    if (!form.getValues('expiryDate')) {
      const defaultDate = addYears(new Date(), 1);
      form.setValue('expiryDate', defaultDate);
    }
  }, [form]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">{t('financialDetails', 'Financial Details')}</h3>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="signedDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('signedDate', 'Signed Date')} *</FormLabel>
              <FormControl>
                <DatePicker
                  captionLayout="dropdown-buttons"
                  fromYear={1900}
                  toYear={2100} 
                  selected={field.value}
                  onSelect={(date) => field.onChange(date)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expiryDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('expiryDate', 'Expiry Date')} *</FormLabel>
              <FormControl>
                <DatePicker
                  captionLayout="dropdown-buttons"
                  fromYear={1900}
                  toYear={2100} 
                  selected={field.value}
                  onSelect={(date) => field.onChange(date)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="expectedCost"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {costLabel} *
                {servicesTotal !== undefined && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {t('fromServices', '(from services)')}
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currencyId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('currency', 'Currency')} *</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectCurrency', 'Select currency')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {currencies.length > 0 ? (
                    currencies.map((currency) => (
                      <SelectItem key={currency.id} value={currency.id}>
                        {currency.code} - {currency.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-currencies" disabled>
                      {t('noCurrenciesAvailable', 'No currencies available')}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrencyModalOpen(true);
                }}
              >
                + {t('addNewCurrency', 'Add new currency')}
              </Button>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
